import Component from '@glimmer/component';
import PowerSelect from 'ember-power-select/components/power-select';
import { eq } from 'ember-truth-helpers';

import type {
  DisabledArgs,
  Group,
  Option,
} from '../../types/components/prism/select-input.ts';
import PrismSelectInputOption from './select-input/option.gts';
import PrismSelectInputTrigger from './select-input/trigger.gts';
import PrismTooltip from './tooltip.gts';

type UnsafeArgs = {
  unsafe_hasDropdownMaxHeight?: true;
  // Only for testing purposes
  unsafe_initiallyOpened?: true;
  unsafe_isClampedToOneLine?: true;
  unsafe_isClearable?: true;
  unsafe_matchTriggerWidth?: true;
  unsafe_openOnClick?: true;
  unsafe_renderInPlace?: true;
};

export interface PrismSelectInputSignature<
  Value extends Readonly<Option | null | undefined>,
  Options extends readonly (Option | Group<Option>)[],
> {
  Args: DisabledArgs &
    UnsafeArgs & {
      horizontalPosition?: 'left' | 'center' | 'right';
      isSecondary?: true;
      isSmall?: true;
      onChange: (option: Value) => unknown;
      options: Options;
      placeholder?: string;
      pseudo?: 'hover' | 'active' | 'focus';
      value?: Value | null;
    };
  Element: HTMLDivElement;
}

export default class PrismSelectInput<
  Value extends Readonly<Option | null | undefined>,
  Options extends readonly (Option | Group<Option>)[],
> extends Component<PrismSelectInputSignature<Value, Options>> {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get disabledMessage() {
    return this.args.disabled ? this.args.disabledMessage : undefined;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get sizeClass() {
    if (this.args.isSmall) {
      return 'prism-select-input__trigger--size-small';
    }

    return 'prism-select-input__trigger--size-default';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get unsafe_hasDropdownMaxHeight() {
    if (this.args.unsafe_hasDropdownMaxHeight === undefined) {
      return true;
    }

    return this.args.unsafe_hasDropdownMaxHeight;
  }

  <template>
    <div class="prism-select-input">
      <PowerSelect
        @allowClear={{@unsafe_isClearable}}
        @disabled={{@disabled}}
        @dropdownClass="prism-select-input__dropdown {{if
          this.unsafe_hasDropdownMaxHeight
          'prism-select-input__dropdown--has-max-height'
        }}"
        @eventType={{if @unsafe_openOnClick "click"}}
        @horizontalPosition={{@horizontalPosition}}
        @initiallyOpened={{@unsafe_initiallyOpened}}
        @matchTriggerWidth={{if @unsafe_matchTriggerWidth true false}}
        @onChange={{@onChange}}
        @options={{@options}}
        @placeholder={{@placeholder}}
        @preventScroll={{true}}
        @renderInPlace={{@unsafe_renderInPlace}}
        @selected={{@value}}
        @triggerClass="
    prism-select-input__trigger
    {{this.sizeClass}}
    {{if
          @isSecondary
          'prism-select-input__trigger--is-secondary'
          'prism-select-input__trigger--is-default'
        }}
  "
        @triggerComponent={{component
          PrismSelectInputTrigger
          isSecondary=@isSecondary
          isSmall=@isSmall
          placeholder=@placeholder
          unsafe_isClampedToOneLine=@unsafe_isClampedToOneLine
          unsafe_isClearable=@unsafe_isClearable
        }}
        data-test-pseudo={{@pseudo}}
        {{! Passed before all other properties to disallow overwriting internally set properties, only meant for modifiers/data-test-ids }}
        ...attributes
        as |option select|
      >
        <PrismSelectInputOption
          @badge={{option.badge}}
          @badgeIcon={{option.badgeIcon}}
          @badgeType={{option.badgeType}}
          @disabled={{option.disabled}}
          @disabledMessage={{option.disabledMessage}}
          @icon={{option.icon}}
          @iconBrand={{option.iconBrand}}
          @iconFlag={{option.iconFlag}}
          @label={{option.label}}
          @selected={{eq option select.selected}}
          @unsafe_afterOptionComponent={{option.unsafe_afterOptionComponent}}
          @unsafe_subLabel={{option.unsafe_subLabel}}
        />
      </PowerSelect>

      {{#if this.disabledMessage}}
        <PrismTooltip @text={{this.disabledMessage}} />
      {{/if}}
    </div>
  </template>
}
